import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { mockRestaurantStock, mockStockMovements, mockBranches } from "@/mocks/restaurantStock";
import * as XLSX from "xlsx";

interface StockItem {
  id: string;
  dealer_id: string;
  branch_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  min_stock_level: number;
  created_at: string;
  branch_name?: string;
}

interface StockMovement {
  id: string;
  dealer_id: string;
  stock_id: string;
  branch_id: string;
  type: "in" | "out" | "adjustment";
  quantity: number;
  description: string;
  created_by: string;
  created_at: string;
  product_name?: string;
  branch_name?: string;
}

interface Branch {
  id: string;
  name: string;
}

type TabKey = "stock" | "movements";

export default function RestaurantStockPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId;
  const [stock, setStock] = useState<StockItem[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("stock");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "low" | "out">("all");

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"in" | "out" | "adjustment" | "new">("in");
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);
  const [saving, setSaving] = useState(false);

  const [formProductName, setFormProductName] = useState("");
  const [formQuantity, setFormQuantity] = useState("");
  const [formUnit, setFormUnit] = useState("kg");
  const [formMinStock, setFormMinStock] = useState("5");
  const [formBranchId, setFormBranchId] = useState("");
  const [formDescription, setFormDescription] = useState("");

  const fetchData = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");

    try {
      const supabase = getSupabase();

      const { data: branchData, error: brErr } = await supabase
        .from("branches")
        .select("id, name")
        .eq("dealer_id", dealerId)
        .order("name");

      const { data: stockData, error: stErr } = await supabase
        .from("restaurant_stock")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("product_name");

      const { data: movData, error: mvErr } = await supabase
        .from("restaurant_stock_movements")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (brErr) throw brErr;
      if (stErr) throw stErr;
      if (mvErr) throw mvErr;

      const branchMap: Record<string, string> = {};
      const branchList = branchData && branchData.length > 0 ? branchData : (mockBranches as Branch[]);
      branchList.forEach((b: any) => { branchMap[b.id] = b.name; });
      setBranches(branchList);

      if (stockData && stockData.length > 0) {
        setStock(stockData.map((s: any) => ({ ...s, branch_name: branchMap[s.branch_id] || "" })));
      } else {
        setStock(mockRestaurantStock as StockItem[]);
      }

      if (movData && movData.length > 0) {
        setMovements(movData.map((m: any) => ({ ...m, branch_name: branchMap[m.branch_id] || "" })));
      } else {
        setMovements(mockStockMovements as StockMovement[]);
      }
    } catch (e: any) {
      setError(e.message || "Veriler yüklenirken hata oluştu.");
      setBranches(mockBranches as Branch[]);
      setStock(mockRestaurantStock as StockItem[]);
      setMovements(mockStockMovements as StockMovement[]);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredStock = useMemo(() => {
    let result = stock;
    if (branchFilter !== "all") {
      result = result.filter((s) => s.branch_id === branchFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((s) => s.product_name.toLowerCase().includes(q));
    }
    if (statusFilter === "low") {
      result = result.filter((s) => s.quantity > 0 && s.quantity <= s.min_stock_level);
    } else if (statusFilter === "out") {
      result = result.filter((s) => s.quantity <= 0);
    }
    return result;
  }, [stock, branchFilter, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const total = stock.length;
    const lowStock = stock.filter((s) => s.quantity > 0 && s.quantity <= s.min_stock_level).length;
    const outOfStock = stock.filter((s) => s.quantity <= 0).length;
    return { total, lowStock, outOfStock };
  }, [stock]);

  const openStockAction = (item: StockItem, action: "in" | "out" | "adjustment") => {
    setSelectedStock(item);
    setModalType(action);
    setFormQuantity(action === "adjustment" ? String(item.quantity) : "");
    setFormDescription("");
    setShowModal(true);
  };

  const openNewStock = () => {
    setSelectedStock(null);
    setModalType("new");
    setFormProductName("");
    setFormQuantity("");
    setFormUnit("kg");
    setFormMinStock("5");
    setFormBranchId(branches.length > 0 ? branches[0].id : "");
    setFormDescription("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStock(null);
  };

  const handleStockAction = async (e: React.FormEvent) => {
    e.preventDefault();
    const qty = Number(formQuantity);
    if (!qty || qty <= 0 || !dealerId) return;

    setSaving(true);
    try {
      const supabase = getSupabase();

      if (modalType === "new") {
        if (!formProductName.trim() || !formBranchId) return;
        const { error: insertErr } = await supabase.from("restaurant_stock").insert({
          dealer_id: dealerId,
          branch_id: formBranchId,
          product_name: formProductName.trim(),
          quantity: qty,
          unit: formUnit,
          min_stock_level: Number(formMinStock) || 5,
        });
        if (insertErr) throw insertErr;

        if (formDescription.trim()) {
          await supabase.from("restaurant_stock_movements").insert({
            dealer_id: dealerId,
            branch_id: formBranchId,
            type: "in",
            quantity: qty,
            description: formDescription.trim(),
            created_by: user?.fullName || "",
          });
        }
      } else if (selectedStock) {
        let newQty = selectedStock.quantity;
        if (modalType === "in") newQty += qty;
        else if (modalType === "out") newQty = Math.max(0, selectedStock.quantity - qty);
        else if (modalType === "adjustment") newQty = qty;

        const { error: updateErr } = await supabase
          .from("restaurant_stock")
          .update({ quantity: newQty, updated_at: new Date().toISOString() })
          .eq("id", selectedStock.id);
        if (updateErr) throw updateErr;

        await supabase.from("restaurant_stock_movements").insert({
          dealer_id: dealerId,
          stock_id: selectedStock.id,
          branch_id: selectedStock.branch_id,
          type: modalType,
          quantity: qty,
          description: formDescription.trim() || `${modalType === "in" ? "Stok girişi" : modalType === "out" ? "Stok çıkışı" : "Stok düzeltme"} - ${selectedStock.product_name}`,
          created_by: user?.fullName || "",
        });
      }

      closeModal();
      fetchData();
    } catch (e: any) {
      alert(e.message || "İşlem sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    const stockHeader = ["Ürün Adı", "Şube", "Miktar", "Birim", "Min. Stok", "Durum"];
    const stockData = filteredStock.map((s) => [
      s.product_name, s.branch_name || "", s.quantity, s.unit, s.min_stock_level,
      s.quantity <= 0 ? "Tükenmiş" : s.quantity <= s.min_stock_level ? "Düşük Stok" : "Yeterli",
    ]);
    const ws1 = XLSX.utils.aoa_to_sheet([stockHeader, ...stockData]);
    ws1["!cols"] = [{ wch: 30 }, { wch: 25 }, { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, "Stok Durumu");

    const movHeader = ["Tarih", "Ürün", "Şube", "İşlem", "Miktar", "Açıklama", "İşlemi Yapan"];
    const movData = movements.map((m) => [
      new Date(m.created_at).toLocaleString("tr-TR"),
      m.product_name || "", m.branch_name || "",
      m.type === "in" ? "Giriş" : m.type === "out" ? "Çıkış" : "Düzeltme",
      m.quantity, m.description, m.created_by,
    ]);
    const ws2 = XLSX.utils.aoa_to_sheet([movHeader, ...movData]);
    ws2["!cols"] = [{ wch: 18 }, { wch: 30 }, { wch: 25 }, { wch: 10 }, { wch: 8 }, { wch: 35 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Stok Hareketleri");

    const filename = `restoran_stok_raporu_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

  const getStockBarColor = (qty: number, min: number) => {
    if (qty <= 0) return "bg-accent-500";
    if (qty <= min) return "bg-secondary-500";
    return "bg-primary-500";
  };

  const getStockBarWidth = (qty: number, min: number) => {
    const max = Math.max(min * 2, qty + 10);
    return Math.min(100, Math.max(4, (qty / max) * 100));
  };

  if (!user?.hasRestaurantStock) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-line text-3xl text-accent-500"></i>
        </div>
        <h4 className="text-lg font-semibold text-foreground-950 mb-1">Erişim Kısıtlandı</h4>
        <p className="text-sm text-foreground-500 mb-4 text-center max-w-sm">
          Bu sayfaya erişmek için bayi yöneticinizden restoran modülü yetkisi talep edin.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Dashboard'a Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-archive-line text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Stok Yönetimi</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            Restoran malzeme stoklarını takip edin, giriş/çıkış işlemlerini yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line"></i>
            Excel İndir
          </button>
          <button
            onClick={openNewStock}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Yeni Stok Kalemi
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Kalem</p>
          <p className="text-lg font-bold text-foreground-950">{stats.total}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-alert-line text-xs text-secondary-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Düşük Stok</p>
          </div>
          <p className="text-lg font-bold text-secondary-600">{stats.lowStock}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-close-circle-line text-xs text-accent-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Tükenmiş</p>
          </div>
          <p className="text-lg font-bold text-accent-600">{stats.outOfStock}</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        <button
          onClick={() => setActiveTab("stock")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
            activeTab === "stock" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
          }`}
        >
          Stok Durumu
        </button>
        <button
          onClick={() => setActiveTab("movements")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
            activeTab === "movements" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
          }`}
        >
          Stok Hareketleri
        </button>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button>
        </div>
      )}

      {/* Filters */}
      {activeTab === "stock" && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-xs">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Ürün ara..."
                className="w-full pl-8 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
              />
            </div>
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 cursor-pointer"
            >
              <option value="all">Tüm Şubeler</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { key: "all", label: "Tümü" },
              { key: "low", label: "Düşük Stok" },
              { key: "out", label: "Tükenmiş" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key as "all" | "low" | "out")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  statusFilter === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 border border-background-200/70 text-foreground-500 hover:bg-background-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : activeTab === "stock" ? (
        filteredStock.length === 0 ? (
          <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-archive-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 font-medium">Stok kalemi bulunamadı</p>
            <p className="text-xs text-foreground-400 mt-1">Filtreleri değiştirin veya yeni stok kalemi ekleyin</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ürün Adı</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Şube</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Miktar</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Stok Seviyesi</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Min. Stok</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStock.map((item) => (
                    <tr key={item.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground-900">{item.product_name}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">
                        {item.branch_name || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className={`font-semibold text-sm ${
                            item.quantity <= 0 ? "text-accent-600" : item.quantity <= item.min_stock_level ? "text-secondary-600" : "text-foreground-950"
                          }`}>
                            {item.quantity}
                          </span>
                          <span className="text-xs text-foreground-400">{item.unit}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-full max-w-[100px] h-2 bg-background-200/70 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${getStockBarColor(item.quantity, item.min_stock_level)}`}
                            style={{ width: `${getStockBarWidth(item.quantity, item.min_stock_level)}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground-500">{item.min_stock_level} {item.unit}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openStockAction(item, "in")}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-primary-600 hover:bg-primary-50 cursor-pointer"
                            title="Stok Girişi"
                          >
                            <i className="ri-add-circle-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => openStockAction(item, "out")}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-accent-600 hover:bg-accent-50 cursor-pointer"
                            title="Stok Çıkışı"
                          >
                            <i className="ri-indeterminate-circle-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => openStockAction(item, "adjustment")}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 cursor-pointer"
                            title="Stok Düzelt"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : movements.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-exchange-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 font-medium">Henüz stok hareketi yok</p>
          <p className="text-xs text-foreground-400 mt-1">Stok giriş/çıkış işlemleri burada listelenecek</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ürün</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">İşlem</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Miktar</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Açıklama</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">İşlemi Yapan</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((mov) => (
                  <tr key={mov.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{formatDate(mov.created_at)}</td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-foreground-900 text-xs">{mov.product_name}</span>
                        {mov.branch_name && <p className="text-xs text-foreground-400">{mov.branch_name}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        mov.type === "in"
                          ? "bg-primary-100 text-primary-700"
                          : mov.type === "out"
                            ? "bg-accent-100 text-accent-700"
                            : "bg-foreground-100 text-foreground-600"
                      }`}>
                        {mov.type === "in" ? "Giriş" : mov.type === "out" ? "Çıkış" : "Düzeltme"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-sm text-foreground-950">{mov.quantity}</td>
                    <td className="px-4 py-3 text-foreground-600 text-xs max-w-[200px] truncate">{mov.description}</td>
                    <td className="px-4 py-3 text-foreground-500 text-xs">{mov.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={closeModal}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      modalType === "in" ? "bg-primary-100" : modalType === "out" ? "bg-accent-100" : modalType === "adjustment" ? "bg-foreground-100" : "bg-secondary-100"
                    }`}>
                      <i className={`${
                        modalType === "in" ? "ri-add-circle-line text-primary-600" :
                        modalType === "out" ? "ri-indeterminate-circle-line text-accent-600" :
                        modalType === "adjustment" ? "ri-edit-line text-foreground-600" :
                        "ri-add-line text-secondary-600"
                      }`}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950">
                      {modalType === "in" ? "Stok Girişi" :
                       modalType === "out" ? "Stok Çıkışı" :
                       modalType === "adjustment" ? "Stok Düzeltme" :
                       "Yeni Stok Kalemi"}
                    </h3>
                  </div>
                  <button onClick={closeModal} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {selectedStock && modalType !== "new" && (
                  <div className="bg-background-100 rounded-xl p-3 mb-4 flex items-center gap-3">
                    <i className="ri-archive-line text-foreground-400"></i>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{selectedStock.product_name}</p>
                      <p className="text-xs text-foreground-400">
                        Mevcut Stok: <strong className="text-foreground-700">{selectedStock.quantity} {selectedStock.unit}</strong>
                        {selectedStock.branch_name ? ` · ${selectedStock.branch_name}` : ""}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleStockAction} className="space-y-4">
                  {modalType === "new" && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label>
                        <input
                          type="text"
                          value={formProductName}
                          onChange={(e) => setFormProductName(e.target.value)}
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                          placeholder="Örn: Dana Bonfile"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-500 mb-1">Birim</label>
                          <select
                            value={formUnit}
                            onChange={(e) => setFormUnit(e.target.value)}
                            className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 cursor-pointer"
                          >
                            <option value="kg">kg</option>
                            <option value="lt">lt</option>
                            <option value="adet">adet</option>
                            <option value="paket">paket</option>
                            <option value="gram">gram</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-500 mb-1">Şube *</label>
                          <select
                            value={formBranchId}
                            onChange={(e) => setFormBranchId(e.target.value)}
                            className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 cursor-pointer"
                            required
                          >
                            {branches.map((b) => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-500 mb-1">Başlangıç Miktarı *</label>
                          <input
                            type="number"
                            value={formQuantity}
                            onChange={(e) => setFormQuantity(e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-500 mb-1">Min. Stok Seviyesi</label>
                          <input
                            type="number"
                            value={formMinStock}
                            onChange={(e) => setFormMinStock(e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {modalType !== "new" && (
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">
                        {modalType === "adjustment" ? "Yeni Miktar" : `${modalType === "in" ? "Eklenecek" : "Çıkarılacak"} Miktar`} *
                      </label>
                      <input
                        type="number"
                        value={formQuantity}
                        onChange={(e) => setFormQuantity(e.target.value)}
                        min="1"
                        step="0.01"
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                        required
                        autoFocus
                      />
                      {selectedStock && modalType === "out" && (
                        <p className="text-xs text-foreground-400 mt-1">
                          Çıkış sonrası: {Math.max(0, selectedStock.quantity - (Number(formQuantity) || 0))} {selectedStock.unit}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Açıklama</label>
                    <input
                      type="text"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="İşlem açıklaması"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={!formQuantity || Number(formQuantity) <= 0 || saving}
                      className="flex-1 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Kaydediliyor
                        </span>
                      ) : "Kaydet"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}